import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  UnregisteredRepository,
  RepositoryOwner,
  CatalogInfoGeneration,
  PullRequest,
  UnregisteredRepositoryDetail,
} from '@developer-portal/plugin-unregistered-repos-common';
import { UnregisteredReposApi } from './UnregisteredReposApi';
import {
  RepositoryFilters,
  RepositoryStats,
  DiscoveryStatus,
  AiStatus,
  UpdateOwnerRequest,
  GenerateCatalogInfoRequest,
  CreatePullRequestRequest,
  UpdateStatusRequest,
  BatchGenerationRequest,
  BatchGenerationJob,
  UnassignOwnerResult,
} from './types';

export class UnregisteredReposClient implements UnregisteredReposApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl('unregistered-repos')}`;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}${path}`;

    const response = await this.fetchApi.fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
      ...init,
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getRepositories(filters?: RepositoryFilters): Promise<UnregisteredRepository[]> {
    const searchParams = new URLSearchParams();

    if (filters?.status) {
      for (const status of filters.status) {
        searchParams.append('status[]', status);
      }
    }
    if (filters?.limit) {
      searchParams.append('limit', filters.limit.toString());
    }
    if (filters?.offset) {
      searchParams.append('offset', filters.offset.toString());
    }
    if (filters?.search) {
      searchParams.append('search', filters.search);
    }
    if (filters?.language) {
      searchParams.append('language', filters.language);
    }
    for (const owner of filters?.owner ?? []) {
      searchParams.append('owner', owner);
    }
    if (filters?.noOwners) {
      searchParams.append('noOwners', 'true');
    }
    for (const repoName of filters?.repoNames ?? []) {
      searchParams.append('repoNames', repoName);
    }
    const query = searchParams.toString();
    const path = query ? `/repositories?${query}` : '/repositories';

    return this.request<UnregisteredRepository[]>(path);
  }

  async getRepository(repoSlug: string): Promise<UnregisteredRepositoryDetail> {
    return this.request<UnregisteredRepositoryDetail>(`/repositories/${encodeURIComponent(repoSlug)}`);
  }

  async updateRepositoryStatus(repoSlug: string, request: UpdateStatusRequest): Promise<void> {
    await this.request(`/repositories/${encodeURIComponent(repoSlug)}/status`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  async updateRepositoryOwner(repoSlug: string, request: UpdateOwnerRequest): Promise<void> {
    await this.request(`/repositories/${encodeURIComponent(repoSlug)}/owner`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  async detectOwners(repoSlug: string): Promise<RepositoryOwner[]> {
    return this.request<RepositoryOwner[]>(`/repositories/${encodeURIComponent(repoSlug)}/detect-owners`, {
      method: 'POST',
    });
  }

  async setPrimaryOwner(repoSlug: string, ownerId: string): Promise<void> {
    return this.request<void>(
      `/repositories/${encodeURIComponent(repoSlug)}/owners/${encodeURIComponent(ownerId)}/set-primary`,
      {
        method: 'PUT',
      },
    );
  }

  async unassignOwner(
    repoSlug: string,
    ownerId: string,
    triggerRedetection: boolean = false,
  ): Promise<UnassignOwnerResult> {
    const params = new URLSearchParams();
    if (triggerRedetection) {
      params.append('triggerRedetection', 'true');
    }

    const url = `/repositories/${encodeURIComponent(repoSlug)}/owners/${encodeURIComponent(ownerId)}${
      params.toString() ? `?${params.toString()}` : ''
    }`;

    return this.request<{ message: string; redetectionTriggered: boolean; newOwnersDetected?: number }>(url, {
      method: 'DELETE',
    });
  }

  async generateCatalogInfo(repoSlug: string, request: GenerateCatalogInfoRequest): Promise<CatalogInfoGeneration> {
    return this.request<CatalogInfoGeneration>(`/repositories/${encodeURIComponent(repoSlug)}/generate`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getCatalogInfoGenerations(repoSlug: string): Promise<CatalogInfoGeneration[]> {
    return this.request<CatalogInfoGeneration[]>(`/repositories/${encodeURIComponent(repoSlug)}/generations`);
  }

  async createPullRequest(repoSlug: string, request: CreatePullRequestRequest): Promise<PullRequest> {
    return this.request<PullRequest>(`/repositories/${encodeURIComponent(repoSlug)}/pull-request`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async triggerDiscovery(): Promise<void> {
    await this.request('/scan', {
      method: 'POST',
    });
  }

  async getDiscoveryStatus(): Promise<DiscoveryStatus> {
    return this.request<DiscoveryStatus>('/scan/status');
  }

  async getRepositoryStats(): Promise<RepositoryStats> {
    return this.request<RepositoryStats>('/stats');
  }

  async getAiStatus(): Promise<AiStatus> {
    return this.request<AiStatus>('/ai/status');
  }

  // Batch generation methods
  async createBatchGenerationJob(request: BatchGenerationRequest): Promise<BatchGenerationJob> {
    return this.request<BatchGenerationJob>('/batch/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getBatchGenerationJobs(filters?: {
    status?: string;
    createdBy?: string;
    limit?: number;
    offset?: number;
  }): Promise<BatchGenerationJob[]> {
    const searchParams = new URLSearchParams();

    if (filters?.status) {
      searchParams.append('status', filters.status);
    }
    if (filters?.createdBy) {
      searchParams.append('createdBy', filters.createdBy);
    }
    if (filters?.limit) {
      searchParams.append('limit', filters.limit.toString());
    }
    if (filters?.offset) {
      searchParams.append('offset', filters.offset.toString());
    }

    const query = searchParams.toString();
    const path = query ? `/batch/jobs?${query}` : '/batch/jobs';

    return this.request<BatchGenerationJob[]>(path);
  }

  async getBatchGenerationJob(jobId: string): Promise<BatchGenerationJob> {
    return this.request<BatchGenerationJob>(`/batch/jobs/${encodeURIComponent(jobId)}`);
  }

  async cancelBatchGenerationJob(jobId: string): Promise<void> {
    await this.request(`/batch/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
    });
  }
}
